import { Box, Collapse, Typography, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSites } from 'store'

import { SoftButton } from '../../Infras/CompleteDetailView/components/Widget'
import useGoToMap from '../../../use-go-to-map'
import { getEquipmentInfo, getNodeStatus, getModelName } from '../utils'
import { isRackType } from '../../../helpers'
import { DetailCardWrapper, DetailRow } from '../Styled'

const STATUS_NODE_COLORS = {
  up: { fill: '#E6F0D7', stroke: '#92BC62' },
  down: { fill: '#FEE2E2', stroke: '#E57373' },
  missing_ref: { fill: '#E7E7E7', stroke: '#9CA3AF' },
}

const DetailLine = ({ label, value, mono }) => (
  <DetailRow>
    <Typography sx={{ fontSize: 11, color: '#292929', fontWeight: 400, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F7F7F7', mx: 1 }} />
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 400,
        color: '#656565',
        fontFamily: mono ? 'Consolas, monospace' : undefined,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '55%',
      }}
    >
      {value || 'N/A'}
    </Typography>
  </DetailRow>
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TopologyDetailCard = ({ equipment, hiddenChildren = [], onClose }) => {
  const { t } = useTranslation()
  const [{selected}] = useSites()
  const [hiddenExpanded, setHiddenExpanded] = useState(false)
  const { go: goToMap } = useGoToMap()
  const open = Boolean(equipment)

  if (!open) return <DetailCardWrapper open={false} />

  const equipInfo = getEquipmentInfo(equipment.type)
  const status = getNodeStatus(equipment.is_reachable)
  const { fill: iconBg, stroke: iconColor } = STATUS_NODE_COLORS[status] || STATUS_NODE_COLORS.missing_ref

  return (
    <DetailCardWrapper open>
      <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: '#292929', flex: 1, minWidth: 0 }}>
          {equipment.name || t('supervision.topology.unnamed')}
        </Typography>

        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: iconBg,
            border: `1.5px solid ${iconColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <i className={`${equipInfo.icon} fa-sm`} style={{ color: iconColor, fontSize: 11 }} />
        </Box>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, pt: 1, pb: 1 }}>
        <Stack justifySelf={'flex-end'} sx={{flex: 1}} mr={1.5}>
          <SoftButton icon="fa-light fa-eye" onClick={() => goToMap({mac: equipment.mac_address, siteId: selected[0].sf_id})}>
            {t('supervision.hardware.detail.see_map')}
          </SoftButton>
        </Stack>
        <DetailLine label={t('supervision.topology.detail.type')} value={equipInfo.labelKey ? t(equipInfo.labelKey) : (equipInfo.label || equipment.type)} />
        {!isRackType(equipment.type) && (
          <DetailLine label={t('supervision.topology.detail.mac')} value={equipment.mac_address} mono />
        )}
        {!isRackType(equipment.type) && (
          <DetailLine label={t('supervision.topology.detail.ip')} value={equipment.ip_address} mono />
        )}
        <DetailLine label={t('supervision.topology.detail.model')} value={getModelName(equipment.model)} />
        <DetailLine label={t('supervision.topology.detail.manufacturer')} value={equipment.manufacturer} />
        <DetailLine label={t('supervision.topology.detail.parent_name', 'Nom du parent')} value={equipment.parent_name} />
        <Stack justifySelf={'flex-end'} sx={{flex: 1}} mr={1.5}>
          <SoftButton icon="fa-light fa-eye" onClick={() => goToMap({mac: equipment.parent_mac, siteId: selected[0].sf_id})}>
            {t('supervision.hardware.detail.see_map')}
          </SoftButton>
        </Stack>
        <DetailLine label={t('supervision.topology.detail.parent_mac')} value={equipment.parent_mac} mono />
        <DetailLine label={t('supervision.topology.detail.parent_port', 'Port qparent')} value={equipment.parent_port != null ? String(equipment.parent_port) : null} />
        <DetailLine label={t('supervision.topology.detail.building')} value={equipment.building_name || equipment.building} />
        <DetailLine label={t('supervision.topology.detail.floor')} value={equipment.floor_name || equipment.floor} />

        {hiddenChildren.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Box
              onClick={() => setHiddenExpanded(v => !v)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: '6px',
                cursor: 'pointer', userSelect: 'none',
                '&:hover': { backgroundColor: '#F3F4F6' },
              }}
            >
              <i className={`fa-regular fa-angle-${hiddenExpanded ? 'down' : 'right'} fa-sm`} style={{ color: '#656565', width: 10 }} />
              <i className="fa-regular fa-eye-slash" style={{ color: '#656565', fontSize: 9 }} />
              <Typography sx={{ fontSize: 11, color: '#656565', flex: 1 }}>
                {t('supervision.topology.detail.hidden_children', 'Équipements masqués')}
              </Typography>
              <Box sx={{ fontSize: 10, fontWeight: 600, color: '#656565', backgroundColor: '#F3F4F6', borderRadius: '10px', px: 0.75, py: '1px' }}>
                {hiddenChildren.length}
              </Box>
            </Box>
            <Collapse in={hiddenExpanded} timeout={150}>
              <Box sx={{ px: 2, pb: 1 }}>
                {hiddenChildren.map(child => {
                  const childInfo = getEquipmentInfo(child.type)
                  const childStatus = getNodeStatus(child.is_reachable)
                  const { stroke } = STATUS_NODE_COLORS[childStatus] || STATUS_NODE_COLORS.missing_ref
                  return (
                    <Box key={child.mac_address} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: '3px' }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: stroke, border: `1.5px solid ${stroke}`, flexShrink: 0 }} />
                      <i className={`${childInfo.icon} fa-sm`} style={{ color: '#9ca3af', fontSize: 10, width: 12 }} />
                      <Typography noWrap sx={{ fontSize: 11, color: '#292929', flex: 1 }}>
                        {child.name || t('supervision.topology.unnamed')}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </DetailCardWrapper>
  )
}

export default TopologyDetailCard


